/**
 * 지역(시·도/구·군) 허브 페이지 — /region/:si/:gu (2026-06-05 A-1, SEO 롱테일).
 * 사용자용 렌더. 크롤러용 SSR 본문은 backend region_seo_view 가 별도 주입.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { propertyApi, Property } from '../services/api';
import { formatUnits } from '../domain/property/formatters';

const RegionPage: React.FC = () => {
  const { si = '', gu = '' } = useParams();
  const siDec = decodeURIComponent(si);
  const guDec = decodeURIComponent(gu);
  const siShort = useMemo(
    () => siDec.replace(/(특별자치도|특별시|광역시|자치도)/g, '').trim() || siDec,
    [siDec],
  );
  const regionLabel = `${siShort} ${guDec}`.trim();

  const [list, setList] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError(false);
      try {
        const all = await propertyApi.getMapData();
        const filtered = all
          .filter((p) => {
            const a = p.address || '';
            return a.includes(guDec) && (a.includes(siShort) || a.includes(siDec));
          })
          .sort((a, b) => (b.is_special ? 1 : 0) - (a.is_special ? 1 : 0));
        if (alive) setList(filtered);
      } catch {
        if (alive) setError(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [guDec, siShort, siDec]);

  useSeoMeta({
    title: `${regionLabel} 신규 분양 단지 ${list.length || ''}곳 | 분양가·청약일정 | 부냥`.replace(' 곳', '곳'),
    description: `${regionLabel} 신규 분양·청약 단지 정보를 한 곳에서. 분양가, 청약일정, 분양혜택 비교. 분양상담 1533-8124`,
    robots: 'index, follow, max-snippet:-1, max-image-preview:large',
    ogTitle: `${regionLabel} 신규 분양 단지 | 부냥`,
    ogType: 'website',
    ogDescription: `${regionLabel} 신규 분양·청약 단지 정보. 분양상담 1533-8124`,
  });

  return (
    <div className="min-h-screen bg-paper">
      <div className="mx-auto max-w-md px-4 py-6 md:max-w-3xl md:px-6 md:py-8">
        <nav className="mb-3 text-[12px] text-muted">
          <Link to="/" className="hover:text-ink">부냥</Link> · {regionLabel} 분양
        </nav>
        <h1 className="text-[20px] font-extrabold text-ink md:text-[24px]">{regionLabel} 신규 분양 단지</h1>
        <p className="mt-1.5 text-[13px] leading-relaxed text-muted">
          {regionLabel}의 신규 분양·청약 단지를 한눈에 비교하세요. 분양가·청약일정·혜택 정보 제공.
        </p>

        {loading ? (
          <div className="mt-10 text-center text-[13px] text-muted">불러오는 중…</div>
        ) : error ? (
          <div className="mt-10 text-center text-[13px] text-muted">
            일시적인 오류입니다. <Link to="/" className="text-accent underline">전체 지도 보기</Link>
          </div>
        ) : list.length === 0 ? (
          <div className="mt-10 text-center text-[13px] text-muted">
            해당 지역의 분양 단지가 아직 없습니다.{' '}
            <Link to="/" className="text-accent underline">전체 지도 보기</Link>
          </div>
        ) : (
          <ul className="mt-5 flex flex-col gap-2.5">
            {list.map((p) => (
              <li key={p.id}>
                <Link
                  to={`/property/${p.id}`}
                  className="flex items-center justify-between rounded-ca-xl border border-ca-line bg-frame px-4 py-3.5 transition-colors hover:border-accent"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-[14.5px] font-bold text-ink">{p.title}</span>
                    <span className="mt-0.5 block truncate text-[12px] text-muted">
                      {p.address}
                      {p.total_units ? ` · ${formatUnits(p, { short: true })}` : ''}
                    </span>
                  </span>
                  <span className="ml-3 shrink-0 text-[18px] text-mute2">›</span>
                </Link>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-8 text-center">
          <Link to="/" className="text-[13px] font-bold text-accent underline">전체 분양 지도 보기 →</Link>
        </div>
      </div>
    </div>
  );
};

export default RegionPage;
